import React from "react";
import { ChevronRight, History, Loader2, Users } from "lucide-react";
import { consolidationRequest as api } from "../../../api/consolidationApi.js";
import { fetchWithSupabaseAuth } from "../../../lib/supabaseClient.js";
import { billingErrorMessage } from "../../../api/billingApi.js";
import { Field, INPUT, PRIMARY, SECONDARY } from "../Fields.jsx";
import HoursDetail from "./HoursDetail.jsx";
import HoursMatrix, { LONG_DAY_SECONDS, isValidatedDay } from "./HoursMatrix.jsx";
import { driverIdentity, hoursLabel, periodTitle } from "../../../utils/consolidationOverview.js";
import { consolidationPeriodMode } from "../../../utils/consolidationViewStorage.js";
import { DriverPanel, TeamPanel } from "./HoursPanel.jsx";
import HoursPrint from "./HoursPrint.jsx";
import { aggregateHours, periodWords } from "./hoursSummary.js";
import { rememberPeriod } from "../../../hooks/useConsolidationData.js";
import "./HoursSummary.css";
import ConsolidationPopover from "../ConsolidationPopover.jsx";
import "../../TraceabilityBanner.css";

const PREVIEW = 10;

// Période de l'export en clair : « de la semaine du 25 au 31 octobre », « d’octobre 2026 », « du mardi 27 octobre ».
function exportPeriod(from, to) {
  const mode = consolidationPeriodMode(from, to);
  const title = periodTitle(mode, from, to).toLocaleLowerCase("fr");
  return mode === "week" ? `de la ${title}` : mode === "month" ? `${/^[aeiouyàâéèêîôû]/.test(title) ? "d’" : "de "}${title}`
    : mode === "day" ? `du ${title}` : title;
}

async function downloadExcel(from, to) {
  const response = await fetchWithSupabaseAuth("/api/billing/consolidation/hours/export", {
    method: "POST", cache: "no-store", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ from_date: from, to_date: to }),
  });
  if (!response.ok) throw new Error(billingErrorMessage(await response.json().catch(() => null), "Export impossible."));
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url; link.download = `heures-chauffeurs-${from}-${to}.xlsx`;
  document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AdditionalHours({ data, rights, busy, run, onChanged, onClose, selectedEmployeeId, selectedDay }) {
  const [employees, setEmployees] = React.useState([]);
  const [employeeId, setEmployeeId] = React.useState(selectedEmployeeId || "");
  const [day, setDay] = React.useState(selectedDay || data.from_date);
  const [selection, setSelection] = React.useState(null);
  React.useEffect(() => {
    if (!selectedDay) return;
    setEmployeeId(selectedEmployeeId || "");
    setDay(selectedDay);
    setSelection(null);
  }, [selectedEmployeeId, selectedDay]);
  const checkDay = async () => {
    const result = await api(`/hours?${new URLSearchParams({ from_date: day, to_date: day })}`);
    setSelection(result.rows.find(r => r.employee_id === employeeId) || {
      token: result.token, key: `${day}:${employeeId}`, employee_id: employeeId,
      employee_name: employees.find(e => e.id === employeeId)?.name, work_date: day,
      sources: [], expected: [], state: "incomplete", non_billable_seconds: "0",
    });
  };
  React.useEffect(() => { const controller = new AbortController(); api("/options", { signal: controller.signal }).then(value => setEmployees(value.employees)).catch(() => {}); return () => controller.abort(); }, []);
  return <div className="grid min-w-0 gap-2 rounded-[4px] border border-slate-200 bg-white p-2">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs font-semibold">Gérer les heures</p>
      <button type="button" className={SECONDARY} disabled={busy} onClick={onClose}>Fermer</button>
    </div>
    <div className="grid items-end gap-2 grid-cols-[minmax(0,18rem)] sm:grid-cols-[minmax(0,18rem)_11rem_auto] sm:justify-start"><Field label="Employé"><select className={INPUT} value={employeeId} disabled={busy} onChange={e => { setEmployeeId(e.target.value); setSelection(null); }}><option value="">Choisir un employé</option>{employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select></Field>
      <Field label="Journée" type="date" min={data.from_date} max={data.to_date} value={day} disabled={busy} onChange={e => { setDay(e.target.value); setSelection(null); }} />
      <button className={`${SECONDARY} h-[30px] justify-self-start`} disabled={busy || !employeeId || !day || day < data.from_date || day > data.to_date} onClick={() => run(checkDay)}>Afficher la journée</button></div>
    {selection ? <HoursDetail key={`${selection.key}:${selection.token}`} row={selection} timezone={data.timezone} rights={rights} busy={busy} run={run} onChanged={async () => { await onChanged(); await checkDay(); }} /> : null}
  </div>;
}

function formatStamp(value, timezone) {
  return value ? new Intl.DateTimeFormat("fr-CA", {
    timeZone: timezone, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }).format(new Date(value)) : "—";
}

function HoursActivity({ activity, timezone, collapsed, busy, onToggle, onMore }) {
  const items = activity?.items || [];
  return <article className="traceability-panel min-w-0 max-w-full rounded-[4px] border border-slate-200 bg-white">
    <div className="traceability-banner">
      <div className="traceability-heading">
        <button type="button" onClick={onToggle} aria-expanded={!collapsed} aria-controls="hours-activity-content"
          aria-label={collapsed ? "Déplier la traçabilité des heures" : "Replier la traçabilité des heures"}
          className="traceability-toggle inline-flex h-7 w-7 items-center justify-center rounded-[4px] border border-white/20 bg-white/10 text-white hover:bg-white/20">
          <ChevronRight size={13} className={collapsed ? "" : "rotate-90"} />
        </button>
        <span className="traceability-icon"><History size={15} /></span>
        <div className="traceability-copy">
          <p>Traçabilité</p>
          <h2>Dernières actions</h2>
        </div>
      </div>
      <div className="traceability-actions">
        <span>{activity ? `${activity.total} action${activity.total === 1 ? "" : "s"}` : "Chargement…"}</span>
      </div>
    </div>
    {!collapsed ? <div id="hours-activity-content" className="min-w-0">
      {!items.length ? <p className="text-[11px] leading-5 text-slate-500">Aucune action enregistrée pour les heures.</p> : (
        <div className="min-w-0 overflow-x-auto rounded-[4px] border border-slate-200/90">
          <table className="w-full min-w-0 table-fixed border-collapse text-[11px] text-slate-700" aria-label="Traçabilité des heures">
            <thead className="sticky top-0 bg-slate-50"><tr>
              <th className="min-w-0 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Date</th>
              <th className="min-w-0 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Utilisateur</th>
              <th className="min-w-0 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Action</th>
              <th className="min-w-0 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Détail</th>
            </tr></thead>
            <tbody>{items.map(entry => <tr key={entry.id} className="odd:bg-slate-50/50">
              <td className="min-w-0 px-2 py-2 text-[10.5px] leading-4 text-slate-500">{formatStamp(entry.occurred_at, timezone)}</td>
              <td className="min-w-0 px-2 py-2"><span className="block overflow-hidden text-ellipsis" title={entry.actor}>{entry.actor}</span></td>
              <td className="min-w-0 px-2 py-2"><span className="block overflow-hidden text-ellipsis font-semibold text-slate-900" title={entry.action}>{entry.action}</span></td>
              <td className="min-w-0 px-2 py-2"><span className="block overflow-hidden text-ellipsis text-slate-600" title={entry.detail}>{entry.detail}</span></td>
            </tr>)}</tbody>
          </table>
        </div>
      )}
      {activity && items.length < activity.total ? <button type="button" className={`${SECONDARY} mt-2`} disabled={busy} onClick={onMore}>Charger plus</button> : null}
      <p className="mt-2 text-[10px] leading-4 text-slate-400">L’export Excel est journalisé après génération. Il ne remet pas les heures à la comptabilité.</p>
    </div> : null}
  </article>;
}

export default function HoursView({ from, to, day = from, selected: selectedParam = "", rights, onHeader, onOpenSheet, onOpenSummary, cache }) {
  const query = new URLSearchParams({ from_date: from, to_date: to }).toString();
  // Une période déjà vue s'affiche dès le premier rendu, sans écran de chargement.
  const [result, setData] = React.useState(() => cache?.get(query) || null), [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const data = result?.from_date === from && result?.to_date === to ? result : null;
  const [busy, setBusy] = React.useState(false), [selected, setSelected] = React.useState(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState(() => selectedParam.startsWith("driver:") ? selectedParam.slice(7) : null);
  const [printing, setPrinting] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [activity, setActivity] = React.useState(null);
  const [activityCollapsed, setActivityCollapsed] = React.useState(true);
  const controller = React.useRef(null);
  const detailRef = React.useRef(null);
  React.useEffect(() => { if (selected || selectedEmployee) detailRef.current?.scrollIntoView({ block: "nearest" }); }, [selected, selectedEmployee]);
  const loadActivity = React.useCallback(async (offset = 0, signal) => {
    const result = await api(`/hours/activity?${new URLSearchParams({ limit: String(PREVIEW), offset: String(offset) })}`, signal ? { signal } : {});
    if (signal?.aborted) return;
    setActivity(current => offset && current ? { ...result, items: [...current.items, ...result.items] } : result);
  }, []);
  const load = React.useCallback(async () => {
    controller.current?.abort();
    const current = new AbortController(); controller.current = current;
    setLoading(true); setError(null);
    try {
      const hours = await api(`/hours?${query}`, { signal: current.signal });
      if (current.signal.aborted) return;
      if (cache) rememberPeriod(cache, query, hours);
      setData(hours); setLoading(false);
      await loadActivity(0, current.signal);
    } catch (e) {
      if (e.name !== "AbortError" && !current.signal.aborted) { setError(e); throw e; }
    } finally {
      if (!current.signal.aborted) setLoading(false);
    }
  }, [query, loadActivity, cache]);
  React.useEffect(() => {
    // Une période déjà vue s'affiche aussitôt, actions bloquées jusqu'au rechargement.
    setData(cache?.get(query) || null); setSelected(null); setPrinting(false); setError(null); setAdding(false);
    load().catch(() => {}); // load owns errors so aborted requests cannot overwrite this period.
    return () => controller.current?.abort();
  }, [load]);
  const run = async work => { setBusy(true); setError(null); try { await work(); } catch (e) { setError(e); } finally { setBusy(false); } };
  const rows = data?.rows || [];
  // A driver named only by name in Journées keeps his line highlighted here.
  const wanted = selectedParam.startsWith("driver:") ? selectedParam.slice(7) : null;
  const wantedEmployee = wanted && rows.find(row => row.employee_id === wanted || driverIdentity(row.employee_name) === driverIdentity(wanted))?.employee_id;
  // Applied once per link: a week change keeps the driver clicked since then.
  const appliedParam = React.useRef(null);
  React.useEffect(() => {
    if (!wantedEmployee || appliedParam.current === selectedParam) return;
    appliedParam.current = selectedParam;
    setSelectedEmployee(wantedEmployee);
  }, [wantedEmployee, selectedParam]);
  const validatedSeconds = rows.filter(isValidatedDay).reduce((sum, row) => sum + Number(row.total_seconds || 0), 0);
  const waiting = rows.filter(row => !isValidatedDay(row));
  const longDays = rows.filter(row => Number(row.total_seconds) > LONG_DAY_SECONDS);
  const employeeCount = new Set(rows.filter(isValidatedDay).map(row => row.employee_id || row.key)).size;
  const firstWaiting = [...waiting].sort((a, b) => a.work_date.localeCompare(b.work_date))[0];
  React.useEffect(() => {
    if (!data) return;
    onHeader?.({ todo: waiting.length || null, kpis: [
      { label: "Heures validées", value: hoursLabel(validatedSeconds), sub: `${employeeCount} chauffeur${employeeCount > 1 ? "s" : ""}`, tone: "ok" },
      { label: "Journées en attente", value: String(waiting.length), sub: `${hoursLabel(waiting.reduce((sum, row) => sum + Number(row.total_seconds || 0), 0))} saisies, non validées`, tone: waiting.length ? "wait" : undefined },
      { label: "À vérifier", value: String(longDays.length), sub: "journées de plus de 14 h", tone: longDays.length ? "alert" : undefined },
    ] });
  }, [data, validatedSeconds, waiting.length, longDays.length, employeeCount]);
  const detail = data?.rows.find(row => row.key === selected);
  const employees = data ? aggregateHours(data.rows).employees : null;
  const summaryOf = id => employees?.get(id);
  const summary = summaryOf(selectedEmployee);
  const selectDay = key => { setSelected(key); setSelectedEmployee(null); };
  const selectEmployee = id => { setSelectedEmployee(id); setSelected(null); };
  const blocked = busy || loading;
  const actions = <>
    <ConsolidationPopover key={query} label="Exporter pour la paie (Excel)" className={PRIMARY} disabled={blocked || !data} closeLabel="Annuler">
      {({ close }) => <>
        <p className="text-xs">Exporter les heures des chauffeurs {exportPeriod(from, to)} ?</p>
        <button type="button" className={`${PRIMARY} justify-self-start`} disabled={blocked || !data} onClick={() => {
          close();
          run(async () => { await downloadExcel(from, to); await loadActivity(0); });
        }}>Confirmer l’export</button>
      </>}
    </ConsolidationPopover>
    <button type="button" className={SECONDARY} disabled={blocked || !data?.rows.length} onClick={() => setPrinting(true)}>Imprimer les heures</button>
    {rights.hoursManage ? <button className={adding ? PRIMARY : SECONDARY} disabled={blocked || !data} aria-pressed={adding} onClick={() => setAdding(value => !value)}>Gérer les heures</button> : null}
    <button className={SECONDARY} disabled={blocked} onClick={() => load().catch(() => {})}>Actualiser</button>
  </>;
  return <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-2">
    {data && (waiting.length || longDays.length) ? <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-[4px] border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
      <b>Pour boucler {from === to ? "la journée" : "la période"} :</b>
      {waiting.length ? <button type="button" onClick={() => onOpenSheet?.(firstWaiting.work_date, firstWaiting.employee_id)} className="rounded-[4px] border border-amber-300 bg-white px-2 py-0.5 font-semibold text-slate-700 hover:border-amber-400"> {waiting.length} journée{waiting.length > 1 ? "s" : ""} sans relevé validé → Journées du Dispatch</button> : null}
      {longDays.length ? <button type="button" onClick={() => { setSelected(longDays[0].key); setSelectedEmployee(null); }} className="rounded-[4px] border border-amber-300 bg-white px-2 py-0.5 font-semibold text-slate-700 hover:border-amber-400"> {longDays.length} journée{longDays.length > 1 ? "s" : ""} de plus de 14 h à vérifier</button> : null}
    </div> : null}
    {data ? null : <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    {error ? <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error.message}</p> : null}
    {loading ? <div role="status" className="flex items-center gap-2 rounded-[4px] border border-accent bg-accent px-2 py-1.5 text-xs text-white">
      <Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" />
      <span>{data ? "Actualisation des heures…" : "Chargement des heures…"}</span>
    </div> : busy ? <p role="status" className="text-xs text-slate-500">Vérification en cours…</p> : null}
    {!data ? (!loading && error ? <p className="text-sm text-slate-500">Chargement interrompu.</p> : null) : <div aria-busy={loading} className="grid min-w-0 gap-2">
      {/* Grille à gauche, panneau à droite : journée, chauffeur ou équipe, sans défiler jusqu'en bas. */}
      <div className="grid min-w-0 gap-2 min-[1600px]:grid-cols-[minmax(0,1fr)_28rem] min-[1600px]:items-start">
        <div className="grid min-w-0 gap-2">
          <HoursMatrix data={data} day={day} actions={actions} selected={selected} selectedEmployee={selectedEmployee} busy={blocked} onSelect={selectDay} onSelectEmployee={selectEmployee} />
        </div>
        {data.rows.length ? <aside id="consolidation-hours-detail" ref={detailRef} aria-label="Panneau des heures" className="grid min-w-0 gap-2">
          {detail ? <>
            <div className="flex flex-wrap gap-1.5">
              {detail.employee_id && summaryOf(detail.employee_id) ? <button type="button" className={SECONDARY} disabled={blocked} onClick={() => selectEmployee(detail.employee_id)}>← {periodWords(from, to).noun} de {summaryOf(detail.employee_id).name}</button> : null}
              <button type="button" className={SECONDARY} disabled={blocked} onClick={() => selectEmployee(null)}><Users size={13} aria-hidden="true" />Toute l’équipe</button>
            </div>
            <HoursDetail key={`${detail.key}:${detail.token}`} row={detail} timezone={data.timezone} rights={rights} busy={blocked} run={run} onChanged={load} onOpenSheet={onOpenSheet} onOpenSummary={onOpenSummary} />
          </> : summary ? <DriverPanel employee={summary} data={data} onSelectDay={selectDay} onTeam={() => selectEmployee(null)} />
            : <TeamPanel data={data} onSelectEmployee={selectEmployee} onSelectDay={selectDay} />}
        </aside> : null}
      </div>
      {printing ? <HoursPrint key={query} data={data} employeeId={selectedEmployee || detail?.employee_id} onClose={() => setPrinting(false)} /> : null}
      {rights.hoursManage && adding ? <AdditionalHours key={query} data={data} selectedEmployeeId={detail?.employee_id} selectedDay={detail?.work_date} rights={rights} busy={blocked} run={run} onChanged={load} onClose={() => setAdding(false)} /> : null}
      <HoursActivity activity={activity} timezone={data.timezone} collapsed={activityCollapsed} busy={blocked}
        onToggle={() => setActivityCollapsed(value => !value)}
        onMore={() => run(() => loadActivity(activity?.items.length || 0))} />
    </div>}
  </div>;
}
